import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSettings } from "@/lib/useSettings";
import { SEO } from "@/components/site/SEO";
import { getServices } from "@/api/services";
import { getAnnouncements } from "@/api/announcements";

export function AboutPage() {
  const { lang } = useLang();
  const { settings } = useSettings();
  const about = settings.about ?? {};

  const [allServices, setAllServices] = useState<any[]>([]);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getServices(true),
      getAnnouncements(true)
    ])
      .then(([servicesData, announcementsData]) => {
        setAllServices(servicesData);
        setAnnouncements(announcementsData.slice(0, 5));
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load data:', err);
        setLoading(false);
      });
  }, []);

  const title = about[`title_${lang}`] || (lang === "tr" ? "Hakkımızda" : "About Us");
  const description = about[`body_${lang}`] ? about[`body_${lang}`].substring(0, 160) + "..." : undefined;

  if (loading) return <div className="container mx-auto px-4 py-16 min-h-[50vh]"></div>;

  return (
    <div className="container mx-auto px-4 py-12">
      <SEO title={title} description={description} />
      <div className="flex flex-col lg:flex-row gap-10">
        <aside className="hidden lg:block lg:w-1/3 xl:w-1/4 flex-shrink-0">
          <div className="sticky top-28 space-y-6">
            {/* Services */}
            <div className="bg-white shadow-sm border p-5">
              <h3 className="font-bold text-lg text-primary mb-4 pb-2 border-b">
                {lang === "tr" ? "Hizmetlerimiz" : "Our Services"}
              </h3>
              <ul className="space-y-2">
                {allServices.map((item) => {
                  const itemTitle = lang === "tr" ? item.title_tr : item.title_en;
                  return (
                    <li key={item.id}>
                      <Link
                        to={`/services/${item.slug}`}
                        className="block px-3 py-2 rounded-lg transition-colors text-sm text-muted-foreground hover:bg-primary/5 hover:text-primary"
                      >
                        {itemTitle}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Announcements */}
            {announcements.length > 0 && (
              <div className="bg-white shadow-sm border p-5">
                <h3 className="font-bold text-lg text-primary mb-4 pb-2 border-b">
                  {lang === "tr" ? "Duyurular" : "Announcements"}
                </h3>
                <ul className="space-y-3">
                  {announcements.map((item) => {
                    const itemTitle = lang === "tr" ? item.title_tr : item.title_en;
                    const date = new Date(item.created_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                    return (
                      <li key={item.id}>
                        <Link
                          to={`/announcements/${item.id}`}
                          className="block p-3 rounded-lg hover:bg-primary/5 transition-colors group"
                        >
                          <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-1">
                            {itemTitle}
                          </h4>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar size={12} />
                            <span>{date}</span>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <Link
                  to="/announcements"
                  className="block mt-4 pt-3 border-t text-center text-sm text-primary font-medium hover:text-primary-dark transition-colors"
                >
                  {lang === "tr" ? "Tüm Duyurular →" : "All Announcements →"}
                </Link>
              </div>
            )}
          </div>
        </aside>

        {/* Content */}
        <article className="w-full lg:w-2/3 xl:w-3/4">
          <div className="bg-white shadow-sm border p-6">
            <h1 className="text-4xl font-bold text-primary mb-4">{title}</h1>
            <div className="w-20 h-1 bg-primary mb-8 rounded-full" />
            <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-line">{about[`body_${lang}`]}</p>
          </div>
        </article>
      </div>
    </div>
  );
}
